import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { scoreToGrade } from '../utils/GradeUtils';
import { parseAuditModules, AuditModule } from '../utils/parseAuditModules';
import { parseModuleChecks, Check, valueLabel } from '../utils/parseModuleChecks';
import EvidenceReceipt from './EvidenceReceipt';
import PixelGradeBadge from './PixelGradeBadge';
import AuditTerminalView from './AuditTerminalView';
import AgentRemoteLogsView from './AgentRemoteLogsView';

type Severity = 'fail' | 'warn' | 'info' | 'pass' | 'error' | 'unassessed';

const SEVERITY_RANK: Record<Severity, number> = { fail: 0, error: 1, warn: 2, unassessed: 3, info: 4, pass: 5 };

const normalizeStatus = (status: string): Severity => {
  const s = (status || '').toLowerCase();
  if (['red', 'fail', 'critical', 'failed'].includes(s)) return 'fail';
  if (['yellow', 'warn', 'warning', 'weak'].includes(s)) return 'warn';
  if (['green', 'pass', 'success', 'compliant', 'ok'].includes(s)) return 'pass';
  // a module that could determine nothing (agent NotDetermined, PR-1012) is not an error
  if (['notdetermined', 'not_determined', 'unassessed', 'not_assessed'].includes(s)) return 'unassessed';
  if (['blue', 'error', 'unknown'].includes(s)) return 'error';
  if (s === 'info') return 'info';
  return 'error';
};

const severityStyles: Record<Severity, { label: string; text: string; dot: string; bg: string }> = {
  fail: { label: 'FAILED', text: 'text-red-400', dot: 'bg-red-400', bg: 'bg-red-400/10' },
  error: { label: 'ERROR', text: 'text-blue-400', dot: 'bg-blue-400', bg: 'bg-blue-400/10' },
  warn: { label: 'WARNING', text: 'text-yellow-400', dot: 'bg-yellow-400', bg: 'bg-yellow-400/10' },
  info: { label: 'INFO', text: 'text-sky-300', dot: 'bg-sky-300', bg: 'bg-sky-300/10' },
  pass: { label: 'PASS', text: 'text-green-400', dot: 'bg-green-400', bg: 'bg-green-400/10' },
  unassessed: { label: 'NOT ASSESSED', text: 'text-slate-300', dot: 'bg-slate-400', bg: 'bg-slate-400/10' },
};

const checkDot: Record<Check['status'], string> = {
  pass: 'bg-green-400',
  warn: 'bg-yellow-400',
  fail: 'bg-red-400',
  info: 'bg-white/30',
  unassessed: 'bg-slate-400',
};

const checkStatusLabel: Record<Check['status'], string> = {
  pass: 'Compliant',
  warn: 'Needs Attention',
  fail: 'Non-Compliant',
  info: 'Informational',
  unassessed: 'Not assessed',
};

const severityBadge: Record<string, { text: string; bg: string; label: string }> = {
  critical: { text: 'text-red-300', bg: 'bg-red-400/15 border-red-400/20', label: 'Critical' },
  high:     { text: 'text-orange-300', bg: 'bg-orange-400/15 border-orange-400/20', label: 'High' },
  medium:   { text: 'text-yellow-300', bg: 'bg-yellow-400/15 border-yellow-400/20', label: 'Medium' },
  low:      { text: 'text-white/40', bg: 'bg-white/[0.04] border-white/[0.06]', label: 'Low' },
};

// PR-1033: a value the agent did not read must be visible as such on the row itself.
const ProvenanceMarker: React.FC<{ check: Check }> = ({ check }) => {
  if (check.provenance === 'inferred') {
    return <span className="text-[9px] px-1.5 py-0.5 rounded border bg-amber-400/10 text-amber-300 border-amber-400/20" title={check.basis || 'A documented default was applied'}>INFERRED</span>;
  }
  if (check.provenance === 'missing') {
    return <span className="text-[9px] px-1.5 py-0.5 rounded border bg-slate-400/10 text-slate-300 border-slate-400/20" title="The value could not be read">NOT COLLECTED</span>;
  }
  return null;
};

const REMEDIATION_HINTS: Array<{ match: RegExp; hint: string }> = [
  { match: /usb|removable/i, hint: 'Set the removable-storage device policy to deny write access, then push via GPO or Intune and re-run the module to confirm.' },
  { match: /local admin|administrator/i, hint: 'Review the Administrators group membership and remove unexpected accounts. Baseline expects the built-in Administrator disabled plus one managed admin.' },
  { match: /password/i, hint: 'Raise the minimum password length to 12+ via domain or local policy and verify complexity requirements remain enabled.' },
  { match: /registry/i, hint: 'Compare flagged autostart entries against the approved baseline; remove or sign unknown entries and re-audit.' },
  { match: /firewall/i, hint: 'Re-enable all firewall profiles and reconcile rule changes against the approved rule set.' },
  { match: /bitlocker|encrypt/i, hint: 'Enable BitLocker on all fixed drives (AES-256) and escrow recovery keys before re-running the module.' },
  { match: /update/i, hint: 'Approve and install outstanding critical updates, then re-run the module to clear the finding.' },
];

const remediationFor = (moduleName: string): string => {
  const found = REMEDIATION_HINTS.find(h => h.match.test(moduleName));
  return found?.hint || 'Review the failing checks below, apply the corrective configuration on the target machine, and re-run the module to verify.';
};

interface ModuleNode extends AuditModule {
  severity: Severity;
  checks: Check[];
}

const VulnerabilitySummary: React.FC<{ vulnData: any }> = ({ vulnData }) => {
  if (!vulnData) return null;

  const findings = vulnData.findings || [];
  if (findings.length === 0 && vulnData.total === 0) return null;

  const severityColor: Record<string, string> = {
    critical: 'text-red-400 bg-red-400/10 border-red-400/20',
    high: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
    medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    low: 'text-white/40 bg-white/[0.04] border-white/[0.06]',
  };

  return (
    <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Vulnerability Summary</h3>
        <div className="flex gap-3">
          {vulnData.critical > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-red-400/10 text-red-400 border border-red-400/20">
              {vulnData.critical} Critical
            </span>
          )}
          {vulnData.high > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-400/10 text-orange-400 border border-orange-400/20">
              {vulnData.high} High
            </span>
          )}
          {vulnData.medium > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/20">
              {vulnData.medium} Medium
            </span>
          )}
          {vulnData.low > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-white/40 border border-white/[0.06]">
              {vulnData.low} Low
            </span>
          )}
        </div>
      </div>
      {findings.length > 0 ? (
        <div className="divide-y divide-white/[0.04]">
          {findings.map((f: any, i: number) => (
            <div key={i} className="px-6 py-3 flex items-start gap-3">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${severityColor[f.severity] || severityColor.low}`}>
                {f.severity}
              </span>
              <div className="flex-1">
                <div className="text-sm text-white/80">{f.title}</div>
                <div className="text-xs text-white/30 mt-0.5">{f.detail}</div>
              </div>
              <span className="text-[10px] text-white/20 uppercase">{f.module}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center text-white/30 text-sm">
          No vulnerability indicators found in latest audit.
        </div>
      )}
      {vulnData.scannedAt && (
        <div className="px-6 py-2 border-t border-white/[0.04] text-[10px] text-white/20">
          Last assessed: {new Date(vulnData.scannedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
};

const AgentDetailPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<any>(null);
  const [auditHistory, setAuditHistory] = useState<any[]>([]);
  const [latestAudit, setLatestAudit] = useState<any>(null);
  const [vulnData, setVulnData] = useState<any>(null);
  const [debugLogs, setDebugLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'modules' | 'terminal' | 'history' | 'logs' | 'debug'>('modules');

  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [showPassing, setShowPassing] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [receiptFor, setReceiptFor] = useState<{ checkId: string; name: string } | null>(null);

  useEffect(() => {
    if (!agentId) return;
    const fetchData = async () => {
      try {
        const agents = await api.getAgents();
        const found = agents.find((a: any) => a.id === agentId);
        setAgent(found || { id: agentId, hostname: 'Unknown' });

        const history = await api.getAgentAuditHistory(agentId, 10);
        const historyData = Array.isArray(history) ? history : (history as any)?.data || [];
        setAuditHistory(historyData);

        if (historyData.length > 0 && historyData[0].execution_id) {
          const detail = await api.getAuditResult(historyData[0].execution_id);
          setLatestAudit(detail?.data || detail);
        }

        try {
          const vuln = await api.getAgentVulnScan(agentId);
          if (vuln) setVulnData(vuln);
        } catch {
          // Non-fatal — table may not exist yet
        }

        try {
          const dbgLogs = await api.getAgentDebugLogs(agentId);
          const dbgData = Array.isArray(dbgLogs) ? dbgLogs : (dbgLogs as any)?.data || [];
          setDebugLogs(dbgData);
        } catch {
          // Non-fatal — table may not exist yet
        }
      } catch (err) {
        console.error('Failed to load agent details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentId]);

  const modules: ModuleNode[] = useMemo(() => {
    const raw = parseAuditModules(latestAudit);
    return raw
      .map(m => ({
        ...m,
        severity: normalizeStatus(m.status),
        checks: parseModuleChecks(m.details),
      }))
      .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  }, [latestAudit]);

  const nonPassing = useMemo(() => modules.filter(m => m.severity !== 'pass'), [modules]);
  const passing = useMemo(() => modules.filter(m => m.severity === 'pass'), [modules]);

  const selectedModule = useMemo(() => {
    if (!selectedKey) return null;
    return modules.find(m => m.nodeId === selectedKey) || null;
  }, [selectedKey, modules]);

  const counts = useMemo(() => ({
    fail: modules.filter(m => m.severity === 'fail').length,
    warn: modules.filter(m => m.severity === 'warn').length,
    pass: passing.length,
  }), [modules, passing]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-anchor-blue"></div>
      </div>
    );
  }

  const score = latestAudit?.scoring?.overall_score || 0;
  const { grade, color } = scoreToGrade(score);

  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="text-white/40 hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div>
          <h2 className="text-2xl font-medium text-white">{agent?.hostname || agent?.name || 'Agent'}</h2>
          <p className="text-sm text-white/40">
            {agent?.ipAddress && <span>{agent.ipAddress} · </span>}
            {agent?.osVersion && <span>{agent.osVersion} · </span>}
            <span className={agent?.status === 'ONLINE' ? 'text-green-400' : 'text-white/30'}>{agent?.status || 'UNKNOWN'}</span>
          </p>
        </div>
        {score > 0 && (
          <div className="ml-auto flex items-center gap-3">
            <PixelGradeBadge score={score} size={40} />
            <div>
              <div className="text-2xl font-bold" style={{ color }}>{grade}</div>
              <div className="text-xs text-white/40">{score}%</div>
            </div>
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 bg-white/[0.02] rounded-lg p-1">
        {(['modules', 'terminal', 'history', 'logs', 'debug'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-anchor-blue/20 text-anchor-blue'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            {tab === 'modules' ? 'Module Results' : tab === 'terminal' ? 'Terminal Output' : tab === 'history' ? 'Audit History' : tab === 'logs' ? 'Remote Logs' : (
              <>
                Debug Logs
                {debugLogs.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-red-400/20 text-red-400">
                    {debugLogs.length}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
      </div>

      {/* Vulnerability summary */}
      <VulnerabilitySummary vulnData={vulnData} />

      {/* Tab content */}
      {activeTab === 'modules' && (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-4 items-start">
          {/* Treeview */}
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[1fr_110px] px-4 py-2 border-b border-white/[0.06] text-[10px] font-medium text-white/30 uppercase tracking-wider">
              <span>Module / Check</span>
              <span>Result</span>
            </div>

            {modules.length === 0 && (
              <div className="p-8 text-center text-white/30 text-sm">No audit data yet. Run the agent to see module results.</div>
            )}

            {/* Summary counts bar */}
            {modules.length > 0 && (
              <div className="px-4 py-2 border-b border-white/[0.06] flex items-center gap-3 text-[11px]">
                {counts.fail > 0 && <span className="text-red-400">✗ {counts.fail} failed</span>}
                {counts.warn > 0 && <span className="text-yellow-400">! {counts.warn} warning{counts.warn === 1 ? '' : 's'}</span>}
                <span className="text-green-400">✓ {counts.pass} passed</span>
                <span className="text-white/25 ml-auto">{modules.length} modules total</span>
              </div>
            )}

            {/* Non-passing modules */}
            {nonPassing.map(mod => {
              const key = mod.nodeId;
              const modExpanded = expandedModules.has(key);
              const st = severityStyles[mod.severity];
              const isSelected = selectedKey === key;
              const detailSummary = mod.errorMessage
                || mod.checks.find(c => c.status === 'fail')?.name
                || mod.checks.find(c => c.status === 'warn')?.name
                || '';
              return (
                <div key={key}>
                  <div
                    className={`grid grid-cols-[1fr_110px] items-center px-4 py-2 border-b border-white/[0.04] cursor-pointer transition-colors ${
                      isSelected ? 'bg-anchor-blue/10 border-l-2 border-l-anchor-blue' : 'hover:bg-white/[0.02] border-l-2 border-l-transparent'
                    }`}
                    onClick={() => {
                      setSelectedKey(key);
                      setExpandedModules(prev => {
                        const n = new Set(prev);
                        if (n.has(key)) { n.delete(key); } else { n.add(key); }
                        return n;
                      });
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="text-white/25 text-[10px] w-3">{mod.checks.length > 0 ? (modExpanded ? '▼' : '▶') : ''}</span>
                      <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                      <span className="text-sm text-white/85">{mod.name}</span>
                      {detailSummary && <span className="text-[11px] text-white/30 truncate max-w-[200px]">{detailSummary}</span>}
                    </span>
                    <span className={`text-xs font-medium ${st.text}`}>{st.label}</span>
                  </div>
                  {modExpanded && mod.checks.map((check, ci) => (
                    <div key={ci} className="grid grid-cols-[1fr_110px] items-center pl-10 pr-4 py-1.5 border-b border-white/[0.03] bg-white/[0.01]">
                      <span className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${checkDot[check.status]}`} />
                        <span className="text-xs text-white/60">{check.name}</span>
                        {check.severity && severityBadge[check.severity] && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${severityBadge[check.severity].bg} ${severityBadge[check.severity].text}`}>
                            {severityBadge[check.severity].label}
                          </span>
                        )}
                        <ProvenanceMarker check={check} />
                      </span>
                      <span className={`text-[11px] truncate ${check.status === 'fail' ? 'text-red-400/70' : check.status === 'warn' ? 'text-yellow-400/70' : 'text-white/35'}`}>
                        {checkStatusLabel[check.status]}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Passing modules collapsed into one toggle row */}
            {passing.length > 0 && (
              <button
                onClick={() => setShowPassing(prev => !prev)}
                className="w-full text-left px-4 py-2 text-xs text-white/30 hover:text-white/50 border-b border-white/[0.04] transition-colors"
              >
                {showPassing ? '▼' : '▶'} {passing.length} passing module{passing.length === 1 ? '' : 's'}
              </button>
            )}
            {showPassing && passing.map(mod => {
              const key = mod.nodeId;
              const isSelected = selectedKey === key;
              const modExpanded = expandedModules.has(key);
              return (
                <div key={key}>
                  <div
                    onClick={() => {
                      setSelectedKey(key);
                      setExpandedModules(prev => {
                        const n = new Set(prev);
                        if (n.has(key)) { n.delete(key); } else { n.add(key); }
                        return n;
                      });
                    }}
                    className={`grid grid-cols-[1fr_110px] items-center px-4 py-1.5 border-b border-white/[0.03] cursor-pointer transition-colors ${
                      isSelected ? 'bg-anchor-blue/10' : 'hover:bg-white/[0.02]'
                    }`}
                  >
                    <span className="flex items-center gap-2 pl-3">
                      <span className="text-white/25 text-[10px] w-3">{mod.checks.length > 0 ? (modExpanded ? '▼' : '▶') : ''}</span>
                      <span className="w-2 h-2 rounded-full bg-green-400" />
                      <span className="text-xs text-white/55">{mod.name}</span>
                    </span>
                    <span className="text-[11px] font-medium text-green-400">PASS</span>
                  </div>
                  {modExpanded && mod.checks.map((check, ci) => (
                    <div key={ci} className="grid grid-cols-[1fr_110px] items-center pl-12 pr-4 py-1.5 border-b border-white/[0.03] bg-white/[0.01]">
                      <span className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${checkDot[check.status]}`} />
                        <span className="text-xs text-white/60">{check.name}</span>
                        {check.severity && severityBadge[check.severity] && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${severityBadge[check.severity].bg} ${severityBadge[check.severity].text}`}>
                            {severityBadge[check.severity].label}
                          </span>
                        )}
                        <ProvenanceMarker check={check} />
                      </span>
                      <span className="text-[11px] text-green-400/60 truncate">{checkStatusLabel[check.status]}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-5 xl:sticky xl:top-20">
            {!selectedModule ? (
              <p className="text-white/30 text-sm text-center py-10">Select a module in the tree to see details.</p>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2.5 h-2.5 rounded-full ${severityStyles[selectedModule.severity].dot}`} />
                    <h3 className="text-lg font-medium text-white">{selectedModule.name}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${severityStyles[selectedModule.severity].bg} ${severityStyles[selectedModule.severity].text}`}>
                      {severityStyles[selectedModule.severity].label}
                    </span>
                    {selectedModule.score !== undefined && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/50">{selectedModule.score}%</span>
                    )}
                    <span className="text-[11px] text-white/30">{selectedModule.checks.length} checks</span>
                  </div>
                </div>

                {selectedModule.errorMessage && (
                  <p className="text-xs text-red-400/80">{selectedModule.errorMessage}</p>
                )}

                {selectedModule.checks.length > 0 && (
                  <div>
                    <h4 className="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-2">Audit Evidence ({selectedModule.checks.length} checks)</h4>
                    <div className="space-y-3">
                      {selectedModule.checks.map((check, i) => (
                        <div key={i} className={`rounded-lg border p-2.5 ${
                          check.status === 'fail' ? 'border-red-400/15 bg-red-400/[0.03]' :
                          check.status === 'warn' ? 'border-yellow-400/15 bg-yellow-400/[0.03]' :
                          'border-white/[0.06] bg-white/[0.01]'
                        }`}>
                          <div className="flex items-start gap-2 mb-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${checkDot[check.status]}`} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-white/80">{check.name}</span>
                                {check.severity && severityBadge[check.severity] && (
                                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${severityBadge[check.severity].bg} ${severityBadge[check.severity].text}`}>
                                    {severityBadge[check.severity].label}
                                  </span>
                                )}
                                <ProvenanceMarker check={check} />
                                {check.id && latestAudit?.execution_id && (
                                  <button
                                    onClick={() => setReceiptFor({ checkId: check.id as string, name: check.name })}
                                    className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-white/[0.1] text-white/55 hover:text-white/80 hover:bg-white/[0.05]"
                                  >
                                    Receipt
                                  </button>
                                )}
                              </div>
                              <div className={`text-[10px] font-medium mt-0.5 ${
                                check.status === 'fail' ? 'text-red-400' :
                                check.status === 'warn' ? 'text-yellow-400' :
                                check.status === 'pass' ? 'text-green-400' :
                                check.status === 'unassessed' ? 'text-slate-300' : 'text-white/40'
                              }`}>{checkStatusLabel[check.status]}</div>
                            </div>
                          </div>
                          {check.value && (
                            <div className="ml-3.5 mt-1">
                              <span className="text-[9px] font-medium text-white/30 uppercase tracking-wider">{valueLabel(check)}: </span>
                              <span className="text-[11px] text-white/60 break-words">{check.value}</span>
                            </div>
                          )}
                          {check.description && (
                            <div className="ml-3.5 mt-0.5">
                              <span className="text-[9px] font-medium text-white/30 uppercase tracking-wider">Expected: </span>
                              <span className="text-[11px] text-white/50 break-words">{check.description}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedModule.severity !== 'pass' && selectedModule.severity !== 'unassessed' && (
                  <div className={`rounded-lg border p-3 ${selectedModule.severity === 'fail' ? 'border-red-400/20 bg-red-400/[0.04]' : 'border-yellow-400/20 bg-yellow-400/[0.04]'}`}>
                    <h4 className="text-[10px] font-medium text-white/40 uppercase tracking-wider mb-1.5">
                      Remediation{selectedModule.severity === 'fail' ? ' — high priority' : ''}
                    </h4>
                    <p className="text-xs text-white/60 leading-relaxed">{remediationFor(selectedModule.name)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'terminal' && (
        <AuditTerminalView auditResult={latestAudit} modules={modules} />
      )}

      {activeTab === 'history' && (
        <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-white/[0.06]">
            <h3 className="text-lg font-medium text-white">Audit History</h3>
          </div>
          {auditHistory.length === 0 ? (
            <div className="p-8 text-center text-white/30 text-sm">No audit history yet</div>
          ) : (
            <table className="min-w-full divide-y divide-white/[0.06]">
              <thead>
                <tr className="bg-white/[0.02]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {auditHistory.map((audit: any, i: number) => {
                  const s = audit.overall_score || 0;
                  const g = scoreToGrade(s);
                  return (
                    <tr key={audit.execution_id || i} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-3 text-sm text-white/70">
                        {audit.started_at ? new Date(audit.started_at).toLocaleString() : '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-white/70">{s > 0 ? `${s}%` : '—'}</td>
                      <td className="px-6 py-3">
                        {s > 0 && <span className="text-sm font-medium" style={{ color: g.color }}>{g.grade}</span>}
                      </td>
                      <td className="px-6 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          audit.status === 'COMPLETED' ? 'bg-green-400/10 text-green-400' : 'bg-white/[0.04] text-white/40'
                        }`}>{audit.status || '—'}</span>
                      </td>
                      <td className="px-6 py-3 text-sm text-white/40">
                        {audit.duration ? `${(audit.duration / 1000).toFixed(1)}s` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'logs' && agentId && (
        <AgentRemoteLogsView agentId={agentId} />
      )}

      {activeTab === 'debug' && (
        <AgentDebugLogsView debugLogs={debugLogs} />
      )}

      {receiptFor && latestAudit?.execution_id && (
        <EvidenceReceipt
          executionId={latestAudit.execution_id}
          checkId={receiptFor.checkId}
          checkName={receiptFor.name}
          onClose={() => setReceiptFor(null)}
        />
      )}
    </div>
  );
};

const AgentDebugLogsView: React.FC<{ debugLogs: any[] }> = ({ debugLogs }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/[0.06] flex items-center justify-between">
        <h3 className="text-lg font-medium text-white">Debug Logs</h3>
        <span className="text-xs text-white/30">Last 7 entries retained</span>
      </div>
      {debugLogs.length === 0 ? (
        <div className="p-8 text-center text-white/30 text-sm">No debug log entries recorded</div>
      ) : (
        <table className="min-w-full divide-y divide-white/[0.06]">
          <thead>
            <tr className="bg-white/[0.02]">
              <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase w-10"></th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white/40 uppercase">Error Summary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/[0.04]">
            {debugLogs.map((log: any) => {
              const isExpanded = expandedId === log.id;
              return (
                <React.Fragment key={log.id}>
                  <tr
                    className="hover:bg-white/[0.02] transition-colors cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : log.id)}
                  >
                    <td className="px-6 py-3 text-white/25 text-xs">
                      {isExpanded ? '▼' : '▶'}
                    </td>
                    <td className="px-6 py-3 text-sm text-white/70 whitespace-nowrap">
                      {log.occurred_at ? new Date(log.occurred_at).toLocaleString() : '--'}
                    </td>
                    <td className="px-6 py-3 text-sm text-red-400/80">
                      {log.error_summary}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={3} className="px-6 py-4 bg-white/[0.01]">
                        <div className="rounded-lg bg-black/30 border border-white/[0.06] p-4 overflow-x-auto">
                          <pre className="text-xs text-white/60 whitespace-pre-wrap font-mono leading-relaxed max-h-96 overflow-y-auto">
                            {log.log_content}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AgentDetailPage;
